import type { Event } from '../types';
import { metadataObject } from './crowd';

export interface DemoPerson { id: number; name: string; role: 'student' | 'teacher'; badge: string; }
export interface DemoLecture {
  id: string; title: string; room: string; locationId: number; cameraId: number;
  start: string; end: string; teacherId: number; studentIds: number[];
}
export interface AttendanceReview {
  id: string; lectureId: string; personId: number; status: 'absent'; entryEventId: number; reason: string;
  reviewedBy: number; reviewedAt: string; lectureStart: string; lectureEnd: string; cameraId: number; locationId: number;
}
export interface EducationDemo {
  companyId: number; schemaVersion: 1; date: string; timeZone: string;
  people: DemoPerson[]; lectures: DemoLecture[]; eventIds: number[]; locationIds: number[];
  attendanceReviews?: AttendanceReview[];
}
export type EducationConfiguration = Omit<EducationDemo, 'people'> & { people: Pick<DemoPerson, 'id' | 'role'>[] };
export function validateDemo(value: EducationConfiguration, company: number | null): EducationConfiguration {
  if (!value || value.companyId !== company || value.schemaVersion !== 1 || !value.people?.length || !value.lectures?.length) {
    throw new Error('Campus attendance is not configured for this account.');
  }
  new Intl.DateTimeFormat('en', { timeZone: value.timeZone }).format();
  if (!value.locationIds?.length || value.locationIds.some(id => !Number.isInteger(id) || id <= 0)) throw new Error('Invalid campus locations.');
  const ids = value.people.map(p => p.id);
  if (ids.some(id => !Number.isInteger(id) || id <= 0) || new Set(ids).size !== ids.length ||
    value.people.some(person => !['student', 'teacher'].includes(person.role))) throw new Error('Invalid education roster.');
  value.lectures.forEach(lecture => {
    const times = [lecture.start, lecture.end];
    if (!times.every(t => /(Z|[+-]\d\d:\d\d)$/.test(t) && Number.isFinite(Date.parse(t))) ||
      Date.parse(lecture.end) <= Date.parse(lecture.start) || lecture.cameraId <= 0 || lecture.locationId <= 0 ||
      !value.locationIds.includes(lecture.locationId) || !ids.includes(lecture.teacherId) || lecture.studentIds.some(id => !ids.includes(id))) throw new Error('Invalid lecture configuration.');
  });
  return value;
}
export function eventTime(value?: string): number {
  if (!value) return NaN;
  // The API contract returns normalized UTC; old naive UTC values are explicit here.
  return Date.parse(/(Z|[+-]\d\d:\d\d)$/.test(value) ? value : value.replace(' ', 'T') + 'Z');
}
export type EvidenceStatus = 'Classroom evidence' | 'Absent · Reviewed' | 'Review required' | 'Seen on campus only' | 'No matching classroom evidence' | 'Data unavailable';
export function attendanceEvidence(personId: number, lecture: DemoLecture, events: Event[], complete: boolean, reviews: AttendanceReview[] = [], timeZone = 'UTC', now = Date.now()) {
  if (!complete) return { status: 'Data unavailable' as EvidenceStatus, classroom: [] as Event[], campus: [] as Event[], review: undefined as AttendanceReview | undefined };
  const start = Date.parse(lecture.start), end = Date.parse(lecture.end);
  const matchesPerson = (event: Event, confirmed: boolean) => event.registered_persons?.some(p =>
    p.person_id === personId && (confirmed ? p.review_status === 'confirmed' : p.review_status === 'pending'));
  const room = events.filter(event => Number(event.camera_id ?? event.event_camera) === lecture.cameraId &&
    event.event_location === lecture.locationId && eventTime(event.created_date) >= start && eventTime(event.created_date) < end);
  const classroom = room.filter(event => matchesPerson(event, true));
  const campus = events.filter(event => event.event_location === lecture.locationId && matchesPerson(event, true) &&
    eventTime(event.created_date) >= start - 2 * 60 * 60 * 1000 && eventTime(event.created_date) < end);
  const pending = room.some(event => matchesPerson(event, false));
  const day = (value: number) => new Intl.DateTimeFormat('en-CA', { timeZone, year:'numeric', month:'2-digit', day:'2-digit' }).format(new Date(value));
  const review = !classroom.length && !pending ? reviews.filter(item => {
    if (item.personId !== personId || item.lectureId !== lecture.id || item.status !== 'absent' ||
      !lecture.studentIds.includes(personId) || item.cameraId !== lecture.cameraId || item.locationId !== lecture.locationId ||
      Date.parse(item.lectureStart) !== start || Date.parse(item.lectureEnd) !== end || end > now ||
      !(Date.parse(item.reviewedAt) >= end && Date.parse(item.reviewedAt) <= now)) return false;
    const entry = events.find(event => event.event_id === item.entryEventId && matchesPerson(event, true));
    return !!entry && eventTime(entry.created_date) < end && Number.isFinite(eventTime(entry.created_date)) &&
      day(eventTime(entry.created_date)) === day(start) && metadataObject(entry.event_metadata).direction === 'entry';
  }).sort((a,b) => Date.parse(b.reviewedAt)-Date.parse(a.reviewedAt))[0] : undefined;
  if (review && !campus.some(event => event.event_id === review.entryEventId)) campus.unshift(events.find(event => event.event_id === review.entryEventId)!);
  const status: EvidenceStatus = classroom.length ? 'Classroom evidence' : pending ? 'Review required' : review ?
    'Absent · Reviewed' : campus.length ? 'Seen on campus only' : 'No matching classroom evidence';
  return { status, classroom, campus, review };
}
export function csvCell(value: string): string {
  const safe = /^\s*[=+@-]/.test(value) || /^[\t\r\n]/.test(value) ? "'" + value : value;
  return '"' + safe.replace(/"/g, '""') + '"';
}

export function classroomObservation(personId: number, event: Event): string {
  const crossing = metadataObject(event.event_metadata).line_crossing;
  const face = event.registered_persons?.find(p => p.person_id === personId && p.review_status === 'confirmed')?.bounding_box;
  const target = crossing?.target_box;
  const validBox = (box: unknown): box is number[] => Array.isArray(box) && box.length === 4 && box.every(n => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1) && box[2] > 0 && box[3] > 0 && box[0]+box[2] <= 1.001 && box[1]+box[3] <= 1.001;
  if (event.event_type?.toLowerCase().replace(/ /g, '_') === 'line_crossing' && crossing?.direction === 'entry' && validBox(face) && validBox(target)) {
    const x = face[0] + face[2]/2, y = face[1] + face[3]/2;
    if (x >= target[0] && x <= target[0]+target[2] && y >= target[1] && y <= target[1]+target[3]) return 'Entry · Line crossing · Face matched';
  }
  return 'Classroom observation · Face matched';
}

export function vehicleObservations(events: Event[]) {
  const parse = (value: unknown): Record<string, unknown> => {
    try { const obj = typeof value === 'string' ? JSON.parse(value) : value; return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {}; }
    catch { return {}; }
  };
  return events.flatMap(event => {
    const metadata = parse(event.ai_metadata), input = parse(event.event_metadata);
    const vehicles = Array.isArray(metadata.vehicles) ? metadata.vehicles : [];
    return vehicles.filter(vehicle => typeof vehicle?.license_plate === 'string' && vehicle.license_plate.trim()).map(vehicle => ({
      event, plate: String(vehicle.license_plate).trim().toUpperCase(),
      direction: input.direction === 'entry' ? 'Entry' : input.direction === 'exit' ? 'Exit' : 'Observation',
    }));
  }).sort((a, b) => eventTime(a.event.created_date) - eventTime(b.event.created_date));
}
